"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/app/Components/AuthProvider";
import { Button } from "@/app/Components/Button";
import { Field, Input } from "@/app/Components/Field";
import { PageLoading } from "@/app/Components/LoadingBubbles";
import {
  OperationalEvidence,
  OperationalHeader,
  OperationalPage,
  OperationalSection,
  OperationalState,
  OperationalStatus,
  Surface,
} from "@/app/Components/OperationalPage";
import { supabase } from "@/app/lib/supabase";
import { formatUsPhoneInput, normalizeUsPhoneE164 } from "./usPhone";

type EnrollmentStatus =
  | "unavailable"
  | "verification_pending"
  | "verified_without_consent"
  | "ready"
  | "revoked";

type EnrollmentResponse = {
  status: EnrollmentStatus;
  displayLast4?: string;
  consentVersion?: string;
  disclosureRevision?: string;
  verificationId?: string;
  expiresAt?: string;
  reason?: string;
  channelEnabled?: boolean;
  canEnableSms?: boolean;
  quietHours?: { mode: "suppress"; timezone: "America/New_York"; start: string; end: string };
};

type EnrollmentAction =
  | { action: "status" }
  | { action: "start-verify"; phoneE164: string }
  | { action: "check-code"; verificationId: string; code: string }
  | {
      action: "grant-consent";
      verificationId: string;
      consentVersion: string;
      disclosureRevision: string;
    }
  | { action: "revoke"; verificationId: string }
  | { action: "set-opening-alerts"; isEnabled: boolean; quietHours: NonNullable<EnrollmentResponse["quietHours"]> };

type EnrollmentErrorPayload = { errorCode?: string; message?: string };

/** Fixture-only defaults; live grantConsent uses server-returned revisions from status. */
const FIXTURE_CONSENT_VERSION = "course-sniper-sms-v1";
const FIXTURE_DISCLOSURE_REVISION = "course-sniper-sms-disclosure-2026-09-05";
const DEFAULT_QUIET_HOURS = { mode: "suppress" as const, timezone: "America/New_York" as const, start: "22:00", end: "08:00" };
const LOGIN_REDIRECT = "/login?redirect=%2Fprofile%3Ftab%3Dsms";

function isEnrollmentResponse(value: unknown): value is EnrollmentResponse {
  return value !== null && typeof value === "object" && "status" in value;
}

function errorMessage(errorCode?: string, fallback?: string) {
  switch (errorCode) {
    case "unauthenticated":
      return "Your session expired. Sign in again to manage SMS settings.";
    case "unavailable":
      return "SMS setup is unavailable until the server configuration is approved.";
    case "verification_expired":
      return "That code expired. Start a new verification to receive another code.";
    case "verification_rejected":
      return "That code was not accepted. Check it and try again, or start over.";
    case "consent_version_stale":
      return "The SMS disclosure changed. Refresh this page and review the current disclosure.";
    case "already_revoked":
      return "This phone is already removed from SMS alerts.";
    case "rate_limited":
      return "Too many attempts. Wait before trying again.";
    case "invalid_request":
      return "Check the information and try again.";
    default:
      return fallback || "SMS settings could not be updated. Try again.";
  }
}

function unavailableCopy(reason?: string) {
  switch (reason) {
    case "compliance_review_pending":
      return "SMS delivery is waiting for its compliance review.";
    case "budget_enforcement_pending":
      return "SMS delivery is waiting for its budget controls.";
    case "provider_not_configured":
    default:
      return "SMS delivery has not been configured for this environment.";
  }
}

function maskedPhone(last4?: string) {
  return last4 ? `••• ••• ${last4}` : "A verified phone";
}

async function callEnrollment(action: EnrollmentAction): Promise<EnrollmentResponse> {
  const { data, error } = await supabase.functions.invoke("sms-enrollment", { body: action });
  const payload = data as EnrollmentResponse | EnrollmentErrorPayload | null;
  if (error || !isEnrollmentResponse(payload)) {
    const response = (error as { context?: unknown } | null)?.context;
    const details = payload && typeof payload === "object" ? payload as EnrollmentErrorPayload : response instanceof Response ? await response.clone().json().catch(() => undefined) as EnrollmentErrorPayload | undefined : undefined;
    const serviceError = new Error(errorMessage(details?.errorCode, details?.message || error?.message));
    (serviceError as Error & { code?: string }).code = details?.errorCode;
    throw serviceError;
  }
  return payload;
}

function hasServerConsentVersions(response: EnrollmentResponse) {
  return Boolean(response.consentVersion && response.disclosureRevision);
}

function validQuietHours(value: EnrollmentResponse["quietHours"]): value is NonNullable<EnrollmentResponse["quietHours"]> {
  return Boolean(value && /^([01]\d|2[0-3]):[0-5]\d$/.test(value.start) && /^([01]\d|2[0-3]):[0-5]\d$/.test(value.end));
}

export default function SmsEnrollmentClient({ embedded = false }: { embedded?: boolean } = {}) {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!authLoading && !user) router.replace(LOGIN_REDIRECT);
  }, [authLoading, router, user]);

  if (authLoading) {
    return <PageLoading title="Opening SMS settings" description="Checking your signed-in Course Sniper settings." />;
  }
  if (!user) return null;

  // Remount on every owner change. This makes sensitive setup state and every
  // in-flight response owner-scoped rather than briefly rendering a prior user's
  // masked number while React runs an effect.
  return <SmsEnrollmentContent key={user.id} ownerId={user.id} embedded={embedded} />;
}

function SmsEnrollmentContent({
  ownerId,
  invoke = callEnrollment,
  embedded = false,
}: {
  ownerId: string;
  invoke?: (action: EnrollmentAction) => Promise<EnrollmentResponse>;
  embedded?: boolean;
}) {
  const verificationId = useRef<string | null>(null);
  const [enrollment, setEnrollment] = useState<EnrollmentResponse | null>(null);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [consented, setConsented] = useState(false);
  const [changingPhone, setChangingPhone] = useState(false);
  const [removalConfirmed, setRemovalConfirmed] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quietHours, setQuietHours] = useState(DEFAULT_QUIET_HOURS);
  const requestGeneration = useRef(0);

  const applyEnrollment = (next: EnrollmentResponse) => {
    verificationId.current = next.verificationId ?? null;
    if (validQuietHours(next.quietHours)) setQuietHours(next.quietHours);
    setEnrollment(next);
  };

  const refresh = async (generation = requestGeneration.current) => {
    setError(null);
    setQuietHours(DEFAULT_QUIET_HOURS);
    try {
      const next = await invoke({ action: "status" });
      if (generation === requestGeneration.current) applyEnrollment(next);
    } catch (caught) {
      if (generation === requestGeneration.current) setError(caught instanceof Error ? caught.message : "SMS settings could not load.");
    }
  };

  useEffect(() => {
    const generation = ++requestGeneration.current;
    verificationId.current = null;
    setEnrollment(null);
    setPhone("");
    setCode("");
    setConsented(false);
    setChangingPhone(false);
    setRemovalConfirmed(false);
    setPendingAction(null);
    setError(null);
    void refresh(generation);
    return () => {
      // Reject completions after logout, account switch, or unmount.
      requestGeneration.current += 1;
      verificationId.current = null;
    };
  // The authenticated owner is the boundary for this read.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const run = async (name: string, action: EnrollmentAction) => {
    const generation = requestGeneration.current;
    setPendingAction(name);
    setError(null);
    try {
      const next = await invoke(action);
      if (generation !== requestGeneration.current) return null;
      applyEnrollment(next);
      return next;
    } catch (caught) {
      if (generation === requestGeneration.current) setError(caught instanceof Error ? caught.message : "SMS settings could not be updated.");
      return null;
    } finally {
      if (generation === requestGeneration.current) setPendingAction(null);
    }
  };

  const startVerification = async () => {
    const number = normalizeUsPhoneE164(phone);
    if (!number) {
      setError("Enter a US mobile number. Example: (732) 555-0123. We add +1 automatically.");
      return;
    }
    const next = await run("start", { action: "start-verify", phoneE164: number });
    if (next) {
      setPhone("");
      setCode("");
      setChangingPhone(false);
    }
  };

  const checkCode = async () => {
    const currentVerification = verificationId.current;
    if (!currentVerification) {
      setError("Start a new verification to receive a code in this browser session.");
      return;
    }
    if (!/^\d{4,10}$/.test(code.trim())) {
      setError("Enter the 4 to 10 digit verification code.");
      return;
    }
    const next = await run("check", {
      action: "check-code",
      verificationId: currentVerification,
      code: code.trim(),
    });
    if (next) setCode("");
  };

  const grantConsent = async () => {
    const currentVerification = verificationId.current;
    if (!currentVerification) {
      setError("Refresh the status, then verify your phone again before granting consent.");
      return;
    }
    if (!enrollment?.consentVersion || !enrollment.disclosureRevision) {
      setError("The SMS disclosure changed. Refresh this page and review the current disclosure.");
      return;
    }
    await run("consent", {
      action: "grant-consent",
      verificationId: currentVerification,
      consentVersion: enrollment.consentVersion,
      disclosureRevision: enrollment.disclosureRevision,
    });
  };

  const revoke = async () => {
    const currentVerification = verificationId.current;
    if (!currentVerification) {
      setError("Refresh the status before removing your phone.");
      return;
    }
    const next = await run("revoke", { action: "revoke", verificationId: currentVerification });
    if (next) {
      verificationId.current = null;
      setRemovalConfirmed(false);
    }
  };

  const setChannel = async (enabled: boolean) => {
    if (enabled && quietHours.start === quietHours.end) {
      setError("Choose different quiet-hours start and end times before turning on alerts. Matching times suppress alerts all day.");
      return;
    }
    setPendingAction("channel");
    setError(null);
    const next = await run("channel", { action: "set-opening-alerts", isEnabled: enabled, quietHours });
    if (!next) setError("SMS alert preference could not be updated. Refresh status and try again.");
  };

  if (!enrollment && !error) {
    return <PageLoading title="Loading SMS settings" description="Checking whether SMS is available for your account." />;
  }

  const status = enrollment?.status;
  const canUseConfiguredConsent = enrollment ? hasServerConsentVersions(enrollment) : false;
  const showPhoneForm = status === "revoked" || status === "verification_pending" || changingPhone;
  const quietHoursCanEnable = quietHours.start !== quietHours.end;

  const body = (
    <>
      {error ? (
        <OperationalState kind="error" title="SMS settings need attention" description={error} action={<Button intent="secondary" onClick={() => void refresh()}>Refresh status</Button>} />
      ) : null}

      {status === "unavailable" ? (
        <OperationalState
          kind="hard-failure"
          title="SMS setup is unavailable"
          description={unavailableCopy(enrollment?.reason)}
          action={<Button intent="secondary" onClick={() => void refresh()} loading={pendingAction === "refresh"}>Refresh status</Button>}
        />
      ) : null}

      {status && status !== "unavailable" ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(20rem,.9fr)]">
          <OperationalSection title="Delivery status" description="Your server-side eligibility determines whether an alert can be delivered.">
            <Surface>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-[var(--ss-text)]">{status === "ready" ? "Phone and consent recorded" : status === "verified_without_consent" ? "Phone verified; consent still needed" : status === "verification_pending" ? "Verification in progress" : "SMS alerts are off"}</p>
                  <p className="mt-1 text-sm text-[var(--ss-text-muted)]">{status === "ready" ? "Step 4 of 4: choose whether opening alerts are on." : status === "verified_without_consent" ? "Step 3 of 4: review and record SMS consent." : status === "verification_pending" ? "Step 2 of 4: prove you control this phone." : "Step 1 of 4: add a phone when you are ready."}</p>
                  <p className="mt-1 text-sm text-[var(--ss-text-muted)]">{status === "revoked" ? "No phone is active for Course Sniper SMS." : maskedPhone(enrollment?.displayLast4)}</p>
                </div>
                <OperationalStatus tone={status === "ready" ? "positive" : status === "revoked" ? "neutral" : "warning"}>{status === "ready" ? "Consent recorded" : status === "revoked" ? "Off" : "Needs action"}</OperationalStatus>
              </div>
              {status === "ready" && !canUseConfiguredConsent ? <OperationalEvidence tone="warning" className="mt-4">The server no longer recognizes this consent disclosure. SMS remains subject to its server-side gate; refresh or re-verify before relying on alerts.</OperationalEvidence> : null}
              {status === "ready" && enrollment?.reason === "provider_not_configured" ? <OperationalEvidence tone="warning" className="mt-4">SMS delivery is not configured in this environment. You can turn alerts off or remove this phone, but cannot turn alerts on.</OperationalEvidence> : null}
              {status === "ready" ? <OperationalEvidence tone="info" className="mt-4">Alert delivery also requires your SMS channel preference and current server eligibility. <Link href="/scheduling/alerts" className="font-semibold underline">Review Course Sniper monitors</Link>.</OperationalEvidence> : null}
              {status === "ready" ? <div className="mt-4 grid gap-4"><div className="grid gap-3 sm:grid-cols-2"><Field label="Quiet hours start (Eastern)">{(fieldProps) => <Input {...fieldProps} type="time" value={quietHours.start} onChange={(event) => setQuietHours((current) => ({ ...current, start: event.target.value }))} />}</Field><Field label="Quiet hours end (Eastern)">{(fieldProps) => <Input {...fieldProps} type="time" value={quietHours.end} onChange={(event) => setQuietHours((current) => ({ ...current, end: event.target.value }))} />}</Field></div><p className="text-sm text-[var(--ss-text-muted)]">Course Sniper suppresses opening alerts from {quietHours.start} to {quietHours.end} Eastern. Matching times suppress alerts all day.</p><div className="flex flex-wrap gap-2"><Button intent={enrollment?.channelEnabled ? "secondary" : undefined} disabled={!enrollment?.channelEnabled && (!enrollment?.canEnableSms || !quietHoursCanEnable)} loading={pendingAction === "channel"} loadingLabel="Saving" onClick={() => void setChannel(!enrollment?.channelEnabled)}>{enrollment?.channelEnabled ? "Turn off SMS opening alerts" : "Turn on opening alerts"}</Button><Button intent="secondary" onClick={() => { setChangingPhone(true); setError(null); }}>Change phone</Button></div>{!enrollment?.channelEnabled ? <p className="text-sm text-[var(--ss-text-muted)]">Turning on opening alerts saves this quiet-hours choice and explicitly requests SMS only while your server-side eligibility remains active.</p> : <p className="text-sm text-[var(--ss-text-muted)]">Reply STOP to any SMS to opt out. Re-enabling after STOP requires a fresh verification and consent.</p>}</div> : null}
            </Surface>
          </OperationalSection>

          <OperationalSection title="What SMS covers" description="SMS is optional and does not register you for a class.">
            <OperationalEvidence tone="info">Opening alerts point back to Course Sniper for your signed-in, owner-bound review. You must still register manually in WebReg.</OperationalEvidence>
          </OperationalSection>
        </div>
      ) : null}

      {showPhoneForm ? (
        <OperationalSection title={status === "verification_pending" && !changingPhone ? "Step 2: enter your code" : changingPhone ? "Change phone" : "Step 1: add a phone"} description={status === "verification_pending" && !changingPhone ? "Enter the code sent to the number you just submitted. Codes are not retained in this page." : changingPhone ? "A new verification never transfers consent from your current phone. Starting it turns off SMS opening alerts until you verify and consent again." : "US mobile numbers only. Enter your 10-digit number; we add +1 automatically. Your number is sent only to start verification and is cleared from this page after submission."}>
          <Surface>
            {status === "verification_pending" && !changingPhone ? (
              <div className="grid gap-4 sm:max-w-md">
                <Field label="Verification code" hint={enrollment?.expiresAt ? `Code expires ${new Date(enrollment.expiresAt).toLocaleString()}.` : "Codes expire after a limited time."}>
                  {(fieldProps) => <Input {...fieldProps} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} inputMode="numeric" autoComplete="one-time-code" maxLength={10} />}
                </Field>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => void checkCode()} loading={pendingAction === "check"} loadingLabel="Checking">Verify code</Button>
                  <Button intent="secondary" onClick={() => { setChangingPhone(true); setCode(""); }}>Use another phone</Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:max-w-md">
                <Field label="Mobile phone" hint="US numbers only. Example: (732) 555-0123. We add +1 automatically. Standard carrier messaging rates may apply.">
                  {(fieldProps) => (
                    <Input
                      {...fieldProps}
                      type="tel"
                      value={phone}
                      onChange={(event) => setPhone(formatUsPhoneInput(event.target.value))}
                      autoComplete="tel-national"
                      inputMode="tel"
                      placeholder="(732) 555-0123"
                    />
                  )}
                </Field>
                <div className="flex flex-wrap gap-2"><Button onClick={() => void startVerification()} loading={pendingAction === "start"} loadingLabel="Sending code">Send verification code</Button>{changingPhone ? <Button intent="secondary" onClick={() => { setChangingPhone(false); setPhone(""); }}>Keep current phone</Button> : null}</div>
              </div>
            )}
          </Surface>
        </OperationalSection>
      ) : null}

      {status === "verified_without_consent" ? (
        <OperationalSection title="Step 3: review SMS consent" description="Verification does not authorize alerts by itself.">
          <Surface>
            <p className="text-sm leading-6 text-[var(--ss-text-muted)]">By checking this box and continuing, you agree to receive optional Course Sniper SMS opening alerts at {maskedPhone(enrollment?.displayLast4)}. Message frequency varies with your monitors, and message and data rates may apply. Reply STOP to opt out. Visit <Link href="/support" className="font-semibold underline">Support</Link> for help or read the <Link href="/privacy-policy" className="font-semibold underline">Privacy Policy</Link>.</p>
            <label className="mt-4 flex items-start gap-3 text-sm text-[var(--ss-text)]">
              <input type="checkbox" checked={consented} onChange={(event) => setConsented(event.target.checked)} className="mt-1 h-4 w-4 accent-[var(--ss-accent)]" />
              <span>I agree to receive these optional Course Sniper SMS opening alerts.</span>
            </label>
            <div className="mt-4"><Button disabled={!consented || !canUseConfiguredConsent} onClick={() => void grantConsent()} loading={pendingAction === "consent"} loadingLabel="Saving consent">Record SMS consent</Button></div>
          </Surface>
        </OperationalSection>
      ) : null}

      {status === "ready" || status === "verified_without_consent" ? (
        <OperationalSection title="Remove phone and revoke SMS access" description="This turns off SMS opening alerts, revokes this endpoint and consent, and leaves existing Course Sniper monitors in the app.">
          <Surface>
            {!removalConfirmed ? <Button intent="danger" onClick={() => setRemovalConfirmed(true)}>Remove phone and opt out</Button> : <div className="flex flex-wrap items-center gap-3"><p className="text-sm text-[var(--ss-text-muted)]">Remove {maskedPhone(enrollment?.displayLast4)}, turn off opening alerts, and revoke SMS access?</p><Button intent="danger" onClick={() => void revoke()} loading={pendingAction === "revoke"} loadingLabel="Removing">Confirm opt out</Button><Button intent="secondary" onClick={() => setRemovalConfirmed(false)}>Keep phone</Button></div>}
          </Surface>
        </OperationalSection>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-5" data-audit-ready="course-sniper-sms-ready">
        <OperationalSection
          title="SMS opening alerts"
          description="Add a US phone, prove you control it, review consent separately, then choose whether opening alerts are on. Course Sniper still works without SMS."
        >
          <OperationalEvidence tone="info">
            Delivery also requires a current Scarlet Sync subscription eligibility gate when SMS is enabled for your account. <Link href="/profile?tab=billing" className="font-semibold underline">Review billing</Link>.
          </OperationalEvidence>
        </OperationalSection>
        {body}
      </div>
    );
  }

  return (
    <OperationalPage className="pb-28" data-audit-ready="course-sniper-sms-ready">
      <OperationalHeader
        eyebrow="Course Sniper"
        title="SMS settings"
        description="Add a US phone, prove you control it, review consent separately, then choose whether opening alerts are on. Course Sniper still works without SMS."
        actions={<Link href="/scheduling/alerts" className="btn-secondary">Back to Course Sniper</Link>}
      />
      {body}
    </OperationalPage>
  );
}


export type SmsEnrollmentFixtureState = "ready" | "verified_without_consent" | "verification_pending" | "revoked";

/** Deterministic Storybook fixture using the production component and opaque data only. */
export function SmsEnrollmentFixture({ state = "ready" }: { state?: SmsEnrollmentFixtureState }) {
  const invoke = useMemo(() => async (action: EnrollmentAction): Promise<EnrollmentResponse> => {
    const baseline = { verificationId: "11111111-1111-4111-8111-111111111111", displayLast4: "0123", consentVersion: FIXTURE_CONSENT_VERSION, disclosureRevision: FIXTURE_DISCLOSURE_REVISION };
    if (action.action === "status") return state === "ready" ? { status: state, ...baseline, channelEnabled: false, canEnableSms: true, quietHours: DEFAULT_QUIET_HOURS } : { status: state, ...baseline, canEnableSms: true };
    if (action.action === "set-opening-alerts") return { status: "ready", ...baseline, channelEnabled: action.isEnabled, canEnableSms: true, quietHours: action.quietHours };
    if (action.action === "start-verify") return { status: "verification_pending", ...baseline, expiresAt: "2026-09-06T17:00:00.000Z" };
    if (action.action === "check-code") return { status: "verified_without_consent", ...baseline };
    if (action.action === "grant-consent") return { status: "ready", ...baseline, channelEnabled: false, canEnableSms: true, quietHours: DEFAULT_QUIET_HOURS };
    return { status: "revoked" };
  }, [state]);
  return <SmsEnrollmentContent ownerId="fixture-owner" invoke={invoke} />;
}
